/**
 * JAN (Japanese Article Number) and country of origin for product info.
 * Keep in sync with src/utils/productIdentityExtras.ts.
 */

const ORIGIN_KEY =
  /^(country\s*of\s*origin|country\s*of\s*manufacture|countryoforigin|made\s*in)$/i;

function barcodeDigits(value?: string | null): string {
  return String(value || '').replace(/\D/g, '');
}

function isJanCode(digits: string): boolean {
  return digits.length === 13 && (digits.startsWith('45') || digits.startsWith('49'));
}

export function japanArticleNumber(ids: {
  jan?: string | null;
  ean?: string | null;
  gtin?: string | null;
}): string | undefined {
  const explicit = barcodeDigits(ids.jan);
  if (isJanCode(explicit)) return explicit;

  for (const raw of [ids.ean, ids.gtin, ids.jan]) {
    const digits = barcodeDigits(raw);
    const ean13 =
      digits.length === 13 ? digits : digits.length === 14 && digits.startsWith('0') ? digits.slice(1) : '';
    if (isJanCode(ean13)) return ean13;
  }
  return undefined;
}

function originFromNode(node: unknown, depth: number): string | undefined {
  if (depth > 4 || node == null) return undefined;
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = originFromNode(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  if (typeof node !== 'object') return undefined;
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (typeof value === 'string' && ORIGIN_KEY.test(key.trim())) {
      const text = value.trim();
      if (text) return text;
    }
  }
  for (const value of Object.values(node as Record<string, unknown>)) {
    if (value && typeof value === 'object') {
      const found = originFromNode(value, depth + 1);
      if (found) return found;
    }
  }
  return undefined;
}

export function countryOfOriginFromProduct(product: {
  countryOfOrigin?: string | null;
  specifications?: unknown;
}): string | undefined {
  const explicit = String(product.countryOfOrigin || '').trim();
  if (explicit) return explicit;
  return originFromNode(product.specifications, 0);
}
